import React, { useState } from 'react'
import {
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ExternalLink,
  Instagram,
  Music,
  Facebook,
  ImageOff
} from 'lucide-react'
import { ScoutBrandPost } from '@/types/scout'
import scoutService from '@/services/scoutService'

// Platform logo + brand color by platform_id (1=IG, 2=TikTok, 11=Facebook),
// matching the icons used on the Social Dashboard Posts page.
const getPlatformIcon = (platformId: number) => {
  switch (platformId) {
    case 2: return Music        // TikTok
    case 11: return Facebook
    default: return Instagram   // 1
  }
}
const getPlatformColor = (platformId: number): string => {
  switch (platformId) {
    case 2: return 'text-black'       // TikTok
    case 11: return 'text-blue-600'   // Facebook
    default: return 'text-pink-500'   // Instagram
  }
}
// Platform-colored top band (Sprout-style). Instagram uses its brand gradient.
const getPlatformBandColor = (platformId: number): string => {
  switch (platformId) {
    case 2: return 'bg-black'         // TikTok
    case 11: return 'bg-blue-600'     // Facebook
    default: return 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400' // Instagram
  }
}

interface ScoutPostCardProps {
  post: ScoutBrandPost
  customerId: string
}

const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '-'
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const formatEMV = (value: number | string): string => {
  const n = Number(value)
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`
  return `$${n.toFixed(2)}`
}

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const getPlacementBadgeColor = (tier: string | null): string => {
  switch (tier?.toLowerCase()) {
    case 'primary': return 'bg-purple-100 text-purple-700'
    case 'secondary': return 'bg-blue-100 text-blue-700'
    case 'background': return 'bg-gray-100 text-gray-600'
    case 'text_mention': return 'bg-emerald-100 text-emerald-700'
    case 'product_placement': return 'bg-amber-100 text-amber-700'
    default: return 'bg-gray-100 text-gray-600'
  }
}

// Humanize a content_category key (e.g. "game_day_announcement" -> "Game Day Announcement").
const formatContentCategory = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// Map raw media/post types (incl. legacy Apify values like "sidecar", "graphimage")
// to the friendly terms used on the Social Posts page (reel, carousel, image, …).
const formatPostType = (type: string | null | undefined, platformId: number): string => {
  const k = (type || '').toLowerCase()
  if (['sidecar', 'graphsidecar', 'carousel'].includes(k)) return 'carousel'
  if (['reel', 'reels', 'clips'].includes(k)) return 'reel'
  if (['image', 'graphimage', 'photo'].includes(k)) return 'image'
  if (['video', 'graphvideo'].includes(k)) return platformId === 1 ? 'reel' : 'video' // IG feed video = reel
  if (k === 'story') return 'story'
  if (k === 'post' || k === 'feed') return 'post'
  return k.replace(/_/g, ' ')
}

// Below this confidence, the category is flagged "uncertain" (muted style + tooltip).
const CATEGORY_UNCERTAIN_THRESHOLD = 0.6

const ScoutPostCard: React.FC<ScoutPostCardProps> = ({ post, customerId }) => {
  const [imgError, setImgError] = useState(false)

  const handleClick = () => {
    if (post.post_url) {
      window.open(post.post_url, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-300 ${
        post.post_url ? 'cursor-pointer' : ''
      }`}
    >
      {/* Platform-colored top band */}
      <div className={`h-1.5 ${getPlatformBandColor(post.platform_id)}`} />

      {/* Header: platform logo + account name, post date beneath */}
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
        {(() => {
          const PlatformIcon = getPlatformIcon(post.platform_id)
          return <PlatformIcon className={`h-4 w-4 flex-shrink-0 ${getPlatformColor(post.platform_id)}`} />
        })()}
        <div className="min-w-0">
          <div className="text-xs font-semibold text-gray-900 truncate">
            {post.platform_id === 11 ? post.account_username : `@${post.account_username}`}
          </div>
          <div className="text-[10px] text-gray-400">{formatDate(post.posted_at)}</div>
        </div>
      </div>

      {/* Thumbnail */}
      <div className="aspect-video bg-gray-100 relative">
        {post.thumbnail_blob_path && !imgError ? (
          <img
            src={scoutService.getThumbnailUrl(post.post_id, customerId)}
            alt="Post thumbnail"
            className="w-full h-full object-cover"
            crossOrigin="use-credentials"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-1">
            <ImageOff className="h-6 w-6" />
            <span className="text-[10px]">No preview</span>
          </div>
        )}

        {/* Badges overlay */}
        <div className="absolute top-2 left-2 flex gap-1">
          {post.post_type && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-black/60 text-white capitalize">
              {formatPostType(post.post_type, post.platform_id)}
            </span>
          )}
          {post.is_sponsored && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/90 text-white">
              Sponsored
            </span>
          )}
        </div>

        {post.post_url && (
          <div className="absolute top-2 right-2">
            <ExternalLink className="h-3.5 w-3.5 text-white drop-shadow" />
          </div>
        )}

        {/* Content category (what kind of post) */}
        {post.content_category && (() => {
          const uncertain =
            post.content_category_confidence !== null &&
            post.content_category_confidence < CATEGORY_UNCERTAIN_THRESHOLD
          return (
            <div className="absolute bottom-2 left-2">
              <span
                title={
                  uncertain
                    ? `Content category (low confidence: ${Math.round(Number(post.content_category_confidence) * 100)}%)`
                    : 'Content category'
                }
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  uncertain
                    ? 'bg-white/70 text-gray-600 border border-dashed border-gray-400'
                    : 'bg-white/90 text-gray-800'
                }`}
              >
                {formatContentCategory(post.content_category)}
                {uncertain && ' ?'}
              </span>
            </div>
          )
        })()}
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        {/* EMV */}
        <div className="text-xs">
          <span className="text-gray-500">EMV: </span>
          <span className="font-bold text-gray-900">{formatEMV(post.emv ?? 0)}</span>
        </div>

        {/* Engagement */}
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <div className="flex items-center">
            <Heart className="h-3 w-3 mr-0.5 text-rose-500" />
            {formatNumber(post.likes_count)}
          </div>
          <div className="flex items-center">
            <MessageCircle className="h-3 w-3 mr-0.5 text-blue-500" />
            {formatNumber(post.comments_count)}
          </div>
          <div className="flex items-center">
            <Share2 className="h-3 w-3 mr-0.5 text-green-500" />
            {formatNumber(post.shares_count)}
          </div>
          <div className="flex items-center">
            <Eye className="h-3 w-3 mr-0.5 text-purple-500" />
            {formatNumber(post.views_count)}
          </div>
        </div>

        {/* Detection info */}
        <div className="flex items-center">
          <span className="text-[10px] text-gray-500">
            {Math.round(Number(post.confidence) * 100)}% Confidence
          </span>
        </div>
        {/* Detection type & placement */}
        <div className="flex items-center gap-1 flex-wrap">
          {post.detection_type && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
              {post.detection_type.replace(/_/g, ' ')}
            </span>
          )}
          {post.placement_tier && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${getPlacementBadgeColor(post.placement_tier)}`}>
              {post.placement_tier.replace(/_/g, ' ')}
            </span>
          )}
        </div>

        {/* Detection context */}
        {post.detection_context && (
          <p className="text-[10px] text-gray-500 line-clamp-3">{post.detection_context}</p>
        )}
      </div>
    </div>
  )
}

export default ScoutPostCard
